import { create } from 'zustand';

/**
 * Per-layer track type-filter selections, keyed by layer id (== wsEvent type).
 * Each layer maps to the set of currently-selected category values. An empty
 * (or absent) set means "show all" — the filter is purely additive.
 */
interface TrackFilterState {
  /** Selected category values per layer id. */
  selected: Map<string, Set<string>>;

  /** Whether a category is currently selected for a layer. */
  isSelected: (layerId: string, category: string) => boolean;

  /** Flip a single category's selection for a layer. */
  toggle: (layerId: string, category: string) => void;

  /** Replace a layer's selection with an explicit set of categories. */
  setAll: (layerId: string, categories: string[]) => void;

  /** Clear a layer's selection (back to "show all"). */
  clear: (layerId: string) => void;

  /** Read a layer's selected set, if any. */
  selectedFor: (layerId: string) => Set<string> | undefined;
}

/** Store driving which track sub-types are shown within each feed layer. */
export const useTrackFilterStore = create<TrackFilterState>((set, get) => ({
  selected: new Map(),

  isSelected: (layerId, category) => get().selected.get(layerId)?.has(category) ?? false,

  toggle: (layerId, category) =>
    set((state) => withSet(state.selected, layerId, toggleInSet(state.selected.get(layerId), category))),

  setAll: (layerId, categories) =>
    set((state) => withSet(state.selected, layerId, new Set(categories))),

  clear: (layerId) => set((state) => withSet(state.selected, layerId, new Set())),

  selectedFor: (layerId) => get().selected.get(layerId),
}));

/** Return a new set with one value toggled (immutable). */
function toggleInSet(current: Set<string> | undefined, value: string): Set<string> {
  const next = new Set(current);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

/** Return new state with a single layer's selection replaced (immutable). */
function withSet(
  current: Map<string, Set<string>>,
  layerId: string,
  selection: Set<string>,
): { selected: Map<string, Set<string>> } {
  const next = new Map(current);
  next.set(layerId, selection);
  return { selected: next };
}
